import * as React from "react"
import { motion } from "motion/react"
import { Users, Handshake, Building2, DollarSign, UserPlus, CheckCircle, BarChart3, Calendar, Filter, X, ChevronDown } from "lucide-react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { useAuth, type AdminOverviewMetrics, type AdminDealVolumeEntry, type AdminFilterOptions } from "@/contexts/AuthContext"
import { KpiCard } from "./KpiCard"
import { AdminCard } from "./AdminCard"
import { CHART_COLORS, AXIS_STYLE, GRID_STYLE, TOOLTIP_STYLE } from "./AdminChartTheme"

type DatePreset = "today" | "7d" | "30d" | "90d" | "6m" | "1y" | "all"
type ChartPreset = "1d" | "5d" | "30d" | "6m" | "1y"

interface DateRange {
  startDate: string | undefined
  endDate: string | undefined
}

interface Filters {
  agentId: string | undefined
  agencyId: string | undefined
  state: string | undefined
  userType: string | undefined
}

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "6m", label: "6M" },
  { key: "1y", label: "1Y" },
  { key: "all", label: "All Time" },
]

const CHART_PRESETS: { key: ChartPreset; label: string; days: number }[] = [
  { key: "1d", label: "1D", days: 1 },
  { key: "5d", label: "5D", days: 5 },
  { key: "30d", label: "30D", days: 30 },
  { key: "6m", label: "6M", days: 180 },
  { key: "1y", label: "1Y", days: 365 },
]

const USER_TYPE_LABELS: Record<string, string> = {
  agent: "Agent",
  solo_agent: "Solo Agent",
  agency: "Agency",
}

function getDateRange(preset: DatePreset): DateRange {
  const now = new Date()
  const endDate = now.toISOString().split("T")[0]
  
  switch (preset) {
    case "today": {
      const startDate = endDate
      return { startDate, endDate }
    }
    case "7d": {
      const start = new Date(now)
      start.setDate(start.getDate() - 7)
      return { startDate: start.toISOString().split("T")[0], endDate }
    }
    case "30d": {
      const start = new Date(now)
      start.setDate(start.getDate() - 30)
      return { startDate: start.toISOString().split("T")[0], endDate }
    }
    case "90d": {
      const start = new Date(now)
      start.setDate(start.getDate() - 90)
      return { startDate: start.toISOString().split("T")[0], endDate }
    }
    case "6m": {
      const start = new Date(now)
      start.setMonth(start.getMonth() - 6)
      return { startDate: start.toISOString().split("T")[0], endDate }
    }
    case "1y": {
      const start = new Date(now)
      start.setFullYear(start.getFullYear() - 1)
      return { startDate: start.toISOString().split("T")[0], endDate }
    }
    case "all":
    default:
      return { startDate: undefined, endDate: undefined }
  }
}

// Custom select component for filters
function FilterSelect({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder 
}: { 
  label: string
  value: string | undefined
  onChange: (val: string | undefined) => void
  options: Array<{ value: string; label: string }>
  placeholder: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-medium text-white/40 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="appearance-none w-full bg-[#2A2A45] border border-[#3A3A55] rounded-lg px-3 py-2 pr-8 text-xs text-white/80 focus:outline-none focus:border-[#F37020]/50 transition-colors cursor-pointer"
        >
          <option value="">{placeholder}</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
      </div>
    </div>
  )
}

export function AdminOverview() {
  const { getAdminOverview, getAdminDealVolume, getAdminFilterOptions } = useAuth()
  const [metrics, setMetrics] = React.useState<AdminOverviewMetrics | null>(null)
  const [volumeData, setVolumeData] = React.useState<AdminDealVolumeEntry[]>([])
  const [loading, setLoading] = React.useState(true)
  const [chartLoading, setChartLoading] = React.useState(false)
  const [selectedPreset, setSelectedPreset] = React.useState<DatePreset>("all")
  const [chartPreset, setChartPreset] = React.useState<ChartPreset>("30d")
  
  // Filter state
  const [filters, setFilters] = React.useState<Filters>({
    agentId: undefined,
    agencyId: undefined,
    state: undefined,
    userType: undefined,
  })
  const [filterOptions, setFilterOptions] = React.useState<AdminFilterOptions | null>(null)
  const [showFilters, setShowFilters] = React.useState(false)

  // Count active filters
  const activeFilterCount = [filters.agentId, filters.agencyId, filters.state, filters.userType].filter(Boolean).length

  // Fetch filter options on mount
  React.useEffect(() => {
    getAdminFilterOptions().then(res => {
      if (res.success && res.agents && res.agencies && res.states && res.userTypes) {
        setFilterOptions({
          agents: res.agents,
          agencies: res.agencies,
          states: res.states,
          userTypes: res.userTypes,
        })
      }
    })
  }, [getAdminFilterOptions])

  // Fetch KPI metrics when date preset or filters change
  const fetchMetrics = React.useCallback(async (preset: DatePreset, f: Filters) => {
    setLoading(true)
    const { startDate, endDate } = getDateRange(preset)
    const overviewRes = await getAdminOverview(startDate, endDate, f.agentId, f.agencyId, f.state, f.userType)
    if (overviewRes.success && overviewRes.metrics) setMetrics(overviewRes.metrics)
    setLoading(false)
  }, [getAdminOverview])

  // Fetch chart data when chart preset or filters change
  const fetchChartData = React.useCallback(async (preset: ChartPreset, f: Filters) => {
    setChartLoading(true)
    const days = CHART_PRESETS.find(p => p.key === preset)?.days || 30
    const volumeRes = await getAdminDealVolume(days, f.agentId, f.agencyId, f.state, f.userType)
    if (volumeRes.success && volumeRes.data) setVolumeData(volumeRes.data)
    setChartLoading(false)
  }, [getAdminDealVolume])

  // Effect to fetch metrics when preset or filters change
  React.useEffect(() => {
    fetchMetrics(selectedPreset, filters)
  }, [fetchMetrics, selectedPreset, filters])

  // Effect to fetch chart data when chart preset or filters change
  React.useEffect(() => {
    fetchChartData(chartPreset, filters)
  }, [fetchChartData, chartPreset, filters])

  const handlePresetChange = (preset: DatePreset) => {
    setSelectedPreset(preset)
  }

  const handleChartPresetChange = (preset: ChartPreset) => {
    setChartPreset(preset)
  }

  const handleFilterChange = (key: keyof Filters, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const resetFilters = () => {
    setFilters({ agentId: undefined, agencyId: undefined, state: undefined, userType: undefined })
  }

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="text-center py-12 text-white/40 font-admin">Failed to load overview data.</div>
    )
  }

  const chartData = volumeData.map((d) => ({
    ...d,
    submitted: (d.approved || 0) + (d.pending || 0) + (d.rejected || 0),
    label: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }))

  const chartLabel = CHART_PRESETS.find(p => p.key === chartPreset)?.label || "30D"

  // Prepare filter options for dropdowns
  const agentOptions = (filterOptions?.agents || []).map(a => ({ value: a._id, label: `${a.name} (${a.email})` }))
  const agencyOptions = (filterOptions?.agencies || []).map(a => ({ value: a.agencyId, label: a.agencyName }))
  const stateOptions = (filterOptions?.states || []).map(s => ({ value: s, label: s }))
  const userTypeOptions = (filterOptions?.userTypes || []).map(t => ({ value: t, label: USER_TYPE_LABELS[t] || t }))

  return (
    <div className="space-y-4">
      {/* Date Range Filter Bar */}
      <div className="flex items-center gap-2 p-2 bg-[#1C1C32] rounded-lg border border-[#2A2A45]">
        <Calendar className="w-4 h-4 text-white/50 ml-2" />
        <div className="flex gap-1 flex-wrap flex-1">
          {DATE_PRESETS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handlePresetChange(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                selectedPreset === key
                  ? "bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white shadow-lg shadow-[#F37020]/25"
                  : "bg-[#2A2A45] text-white/60 hover:bg-[#3A3A55] hover:text-white/80"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
            showFilters || activeFilterCount > 0
              ? "bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white shadow-lg shadow-[#F37020]/25"
              : "bg-[#2A2A45] text-white/60 hover:bg-[#3A3A55] hover:text-white/80"
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-white/20 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filter Dropdown Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-4 bg-[#1C1C32] rounded-lg border border-[#2A2A45]"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#F37020]" />
              <span className="text-sm font-semibold text-white/80">Filter Data</span>
              {activeFilterCount > 0 && (
                <span className="px-2 py-0.5 text-[10px] font-bold bg-[#F37020]/20 text-[#F37020] rounded-full">
                  {activeFilterCount} active
                </span>
              )}
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white/60 hover:text-white bg-[#2A2A45] hover:bg-[#3A3A55] rounded-lg transition-colors"
              >
                <X className="w-3 h-3" />
                Reset All
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FilterSelect
              label="Agent"
              value={filters.agentId}
              onChange={(val) => handleFilterChange("agentId", val)}
              options={agentOptions}
              placeholder="All Agents"
            />
            <FilterSelect
              label="Agency"
              value={filters.agencyId}
              onChange={(val) => handleFilterChange("agencyId", val)}
              options={agencyOptions}
              placeholder="All Agencies"
            />
            <FilterSelect
              label="State"
              value={filters.state}
              onChange={(val) => handleFilterChange("state", val)}
              options={stateOptions}
              placeholder="All States"
            />
            <FilterSelect
              label="User Type"
              value={filters.userType}
              onChange={(val) => handleFilterChange("userType", val)}
              options={userTypeOptions}
              placeholder="All Types"
            />
          </div>
        </motion.div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Users" value={metrics.totalUsers} icon={Users} delay={0} />
        <KpiCard label="Total Deals" value={metrics.totalDeals} icon={Handshake} delay={0.05} />
        <KpiCard label="Active Agencies" value={metrics.activeAgencies} icon={Building2} delay={0.1} />
        <KpiCard label="Bonus Payout" value={`$${metrics.bonusPayout.toLocaleString()}`} icon={DollarSign} delay={0.15} />
      </div>

      {/* Deal Volume Chart */}
      <AdminCard delay={0.25}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white/70 font-admin">
            Deal Volume ({chartLabel})
          </h3>
          {/* TradingView-style chart toggles */}
          <div className="flex gap-1">
            {CHART_PRESETS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleChartPresetChange(key)}
                disabled={chartLoading}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-all duration-150 ${
                  chartPreset === key
                    ? "bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white shadow-md shadow-[#F37020]/20"
                    : "bg-[#2A2A45] text-white/50 hover:bg-[#3A3A55] hover:text-white/70"
                } ${chartLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-72 relative">
          {chartLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1C1C32]/80 z-10 rounded-lg">
              <div className="w-5 h-5 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradSubmitted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.emerald} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLORS.emerald} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis
                dataKey="label"
                {...AXIS_STYLE}
                tick={{ fill: "#52525B", fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis {...AXIS_STYLE} tick={{ fill: "#52525B", fontSize: 10 }} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="submitted" stroke={CHART_COLORS.emerald} fill="url(#gradSubmitted)" strokeWidth={2} name="Submitted" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </AdminCard>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          label="New Users This Week"
          value={metrics.newUsersThisWeek}
          icon={UserPlus}
          delay={0.3}
        />
        <KpiCard
          label="Processing"
          value="Auto"
          icon={CheckCircle}
          delay={0.35}
        />
        <KpiCard
          label="Avg Deals/Agent"
          value={metrics.avgDealsPerAgent}
          icon={BarChart3}
          delay={0.4}
        />
      </div>
    </div>
  )
}
